import React from 'react';

const InfutorPersonDataDisplay = ({ data }) => {
    // Mapping objects for various field values
    const addressTypes = {
        'Current': 'Current Address',
        'Alt': 'Alternate Address',
        'Previous': 'Previous Address',
        '': ''
    };

    const phoneTypes = {
        'L': 'Land Line',
        'V': 'VoIP',
        'W': 'Wireless',
        'O': 'Other',
        '': ''
    };

    const zipTypes = {
        'F': 'firm or company address',
        'G': 'General delivery address',
        'H': 'High-rise or business complex',
        'P': 'PO Box address',
        'R': 'Rural route address',
        'S': 'Street or residential address',
        '': ''
    };

    const dpvValues = {
        'Y': 'Address DPV confirmed for both primary and (if present) secondary numbers',
        'D': 'Address DPV confirmed for primary number only, secondary number information was missing',
        'S': 'Address DPV confirmed for the primary number only, and secondary number information was present but unconfirmed',
        'N': 'Both Primary and (if present) Secondary number information failed to DPV confirm',
        '': 'Address not presented to hash table'
    };

    const recordTypes = {
        'R': 'Residential',
        'B': 'Business',
        'P': 'Payphone',
        'U': 'Unknown',
        '': ''
    };

    const phoneConfidence = {
        '1': 'highest confidence',
        '2': 'high confidence',
        '3': 'high confidence',
        '4': 'possible disconnects',
        '5': 'likely disconnects',
        '6': 'disconnects',
        '7': 'unconfirmed',
        '': ''
    };

    const daCodes = {
        'y': 'Record as it appears in Directory Assistance',
        'd': 'Record was removed / delisted from Directory Assistance',
        '': 'Private record (not in Directory Assistance)'
    };

    const mapTo = (field, value) => {
        return field[value] ? field[value] : value;
    };

    // Helper function to get nested values safely
    const getNestedValue = (obj, path) => {
        return path.split('.').reduce((current, key) => {
            return current && current[key] !== undefined ? current[key] : '';
        }, obj);
    };

    // Helper function to format array values
    const formatArrayValue = (value) => {
        if (Array.isArray(value)) {
            return value.length > 0 ? value.join(', ') : '';
        }
        return value || '';
    };

    // Helper function to build street address
    const buildStreetAddress = (address) => {
        if (!address) return '';
        
        const parts = [
            address.House,
            address.PreDir,
            address.Street,
            address.StrType,
            address.PostDir,
            address.AptType,
            address.AptNbr
        ].filter(part => part && part.length > 0);
        
        return parts.join(' ');
    };

    // Helper function to show value at index (similar to the PHP show function)
    const show = (input, index) => {
        const isArray = Array.isArray(input);
        let output = '';

        // If the input is an array and if input[index] is not undefined
        if (isArray && typeof input[index] !== 'undefined') {
            output = input[index];
        } else { // If the input is not an array
            output = (index === 0) ? input : output;
        }

        return output || '';
    };

    // Handle both data structures: data.data.Response or data.Response
    const response = data?.data?.Response || data?.Response;
    
    if (!data || !response) {
        return (
            <div className="text-center">
                <h4>No Data Available</h4>
                <p>Reason: No records found</p>
            </div>
        );
    }

    const persons = response.Detail?.Person;
    const requestDate = data.requestDate || data.data?.requestDate;

    if (!persons) {
        return (
            <div className="text-center">
                <h4>Lookup Failed</h4>
                <p>No person data found in response.</p>
            </div>
        );
    }

    // Ensure persons is an array
    const personsArray = Array.isArray(persons) ? persons : [persons].filter(Boolean);

    // Helper function to get primary name (first name object without @attributes or first in array)
    const getPrimaryName = (nameData) => {
        if (!nameData) return {};
        if (Array.isArray(nameData)) {
            // Find first name without @attributes (primary name) or use first one
            const primary = nameData.find(n => !n['@attributes']) || nameData[0];
            return primary || {};
        }
        return nameData;
    };

    // Helper function to get all names (for display)
    const getAllNames = (nameData) => {
        if (!nameData) return [];
        if (Array.isArray(nameData)) {
            return nameData;
        }
        return [nameData];
    };

    return (
        <div className="infutor-person-data-display">
            <div className="row">
                <div className="col-12">
                    {/* Status */}
                    <div className="text-center mb-4">
                        <h3>Status: <span className="text-success">Lookup Successful</span></h3>
                        <p className="text-muted">Found {personsArray.length} person record(s)</p>
                    </div>

                    {/* Iterate through all persons */}
                    {personsArray.map((person, personIndex) => {
                        // Handle Name - can be array or object
                        const allNames = getAllNames(person.Name);
                        const primaryName = getPrimaryName(person.Name);
                        
                        // Handle Address - can be array or object
                        const addresses = Array.isArray(person.Address) 
                            ? person.Address 
                            : person.Address 
                                ? [person.Address] 
                                : [];
                        
                        // Handle Phone - can be array or object
                        const phones = Array.isArray(person.Phone) 
                            ? person.Phone 
                            : person.Phone 
                                ? [person.Phone] 
                                : [];

                        return (
                            <div key={personIndex} className="mb-5">
                                {/* Person Data Section */}
                                <div className="mb-4">
                                    <div className="text-center mb-3">
                                        <span className="glyphicon glyphicon-option-horizontal" />
                                        <span className="glyphicon glyphicon-option-horizontal" />
                                        <span className="glyphicon glyphicon-option-horizontal" />
                                    </div>
                                    <h3 className="text-center">Person #{personIndex + 1}</h3>
                                    <div className="table-responsive">
                                        <table className="table table-striped table-hover">
                                            <thead className="table-dark">
                                                <tr>
                                                    <th>Field Name</th>
                                                    <th>Value</th>
                                                    <th>Description</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr className="table-success">
                                                    <td>&nbsp;</td>
                                                    <td><strong>PERSON #{personIndex + 1}</strong></td>
                                                    <td>&nbsp;</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>PID</strong></td>
                                                    <td>{person.PID || ''}</td>
                                                    <td><em>Personal ID Number</em></td>
                                                </tr>
                                                <tr>
                                                    <td><strong>First Name</strong></td>
                                                    <td>{primaryName.FName || ''}</td>
                                                    <td></td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Middle Name</strong></td>
                                                    <td>{primaryName.MName || ''}</td>
                                                    <td></td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Last Name</strong></td>
                                                    <td>{primaryName.LName || ''}</td>
                                                    <td></td>
                                                </tr>
                                                {allNames.length > 1 && (
                                                    <tr>
                                                        <td><strong>All Names / Aliases</strong></td>
                                                        <td>
                                                            {allNames.map((name, idx) => {
                                                                const nameType = name['@attributes']?.type || 'Primary';
                                                                const fullName = [name.FName, name.MName, name.LName, name.Suffix]
                                                                    .filter(Boolean)
                                                                    .join(' ');
                                                                return (
                                                                    <div key={idx}>
                                                                        <strong>{nameType}:</strong> {fullName}
                                                                    </div>
                                                                );
                                                            })}
                                                        </td>
                                                        <td></td>
                                                    </tr>
                                                )}
                                                <tr>
                                                    <td><strong>SSN</strong></td>
                                                    <td>{formatArrayValue(person.SSN)}</td>
                                                    <td></td>
                                                </tr>
                                                <tr>
                                                    <td><strong>SSN Sequence</strong></td>
                                                    <td>{person.SSNSEQ || ''}</td>
                                                    <td></td>
                                                </tr>
                                                <tr>
                                                    <td><strong>DOB</strong></td>
                                                    <td>{formatArrayValue(person.DOB)}</td>
                                                    <td></td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Last Activity Date</strong></td>
                                                    <td>{person.LastActivityDate || ''}</td>
                                                    <td></td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Deceased</strong></td>
                                                    <td>{person.Deceased || ''}</td>
                                                    <td></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Address Data Section */}
                                {addresses.length > 0 && (
                                    <div className="mb-4">
                                        <div className="text-center mb-3">
                                            <span className="glyphicon glyphicon-option-horizontal" />
                                            <span className="glyphicon glyphicon-option-horizontal" />
                                            <span className="glyphicon glyphicon-option-horizontal" />
                                        </div>
                                        <h3 className="text-center">Address</h3>
                                        <div className="table-responsive">
                                            <table className="table table-striped table-hover">
                                                <thead className="table-dark">
                                                    <tr>
                                                        <th>Field Name</th>
                                                        <th>Value</th>
                                                        <th>Description</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {addresses.map((address, index) => {
                                                        const addressType = address['@attributes']?.type || '';
                                                        return (
                                                            <React.Fragment key={index}>
                                                                <tr className="table-warning">
                                                                    <td>&nbsp;</td>
                                                                    <td><strong>ADDRESS #{index + 1} - {mapTo(addressTypes, addressType)}</strong></td>
                                                                    <td>&nbsp;</td>
                                                                </tr>
                                                                <tr>
                                                                    <td><strong>Street Address</strong></td>
                                                                    <td>{buildStreetAddress(address)}</td>
                                                                    <td></td>
                                                                </tr>
                                                                <tr>
                                                                    <td><strong>City</strong></td>
                                                                    <td>{address.City || ''}</td>
                                                                    <td></td>
                                                                </tr>
                                                                <tr>
                                                                    <td><strong>State</strong></td>
                                                                    <td>{address.State || ''}</td>
                                                                    <td></td>
                                                                </tr>
                                                                <tr>
                                                                    <td><strong>Zip</strong></td>
                                                                    <td>{address.Zip || ''} - {address.Z4 || ''}</td>
                                                                    <td></td>
                                                                </tr>
                                                                <tr>
                                                                    <td><strong>Delivery Point Code</strong></td>
                                                                    <td>{address.DPC || ''}</td>
                                                                    <td></td>
                                                                </tr>
                                                                <tr>
                                                                    <td><strong>Carrier Route</strong></td>
                                                                    <td>{address.CRTE || ''}</td>
                                                                    <td></td>
                                                                </tr>
                                                                <tr>
                                                                    <td><strong>FIPS County Code</strong></td>
                                                                    <td>{address.CNTY || ''}</td>
                                                                    <td></td>
                                                                </tr>
                                                                <tr>
                                                                    <td><strong>Zip Type</strong></td>
                                                                    <td>{mapTo(zipTypes, address.Z4Type)}</td>
                                                                    <td></td>
                                                                </tr>
                                                                <tr>
                                                                    <td><strong>Delivery Point Validation</strong></td>
                                                                    <td>{mapTo(dpvValues, address.DPV)}</td>
                                                                    <td></td>
                                                                </tr>
                                                                <tr>
                                                                    <td><strong>Deliverable</strong></td>
                                                                    <td>{formatArrayValue(address.Deliverable)}</td>
                                                                    <td></td>
                                                                </tr>
                                                                <tr>
                                                                    <td><strong>Last Address Validation Date</strong></td>
                                                                    <td>{address.ValDate || ''}</td>
                                                                    <td></td>
                                                                </tr>
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Phone Data Section - Only show if phone data exists */}
                                {phones.length > 0 && phones[0] && (
                                    <div className="mb-4">
                                        <div className="text-center mb-3">
                                            <span className="glyphicon glyphicon-option-horizontal" />
                                            <span className="glyphicon glyphicon-option-horizontal" />
                                            <span className="glyphicon glyphicon-option-horizontal" />
                                        </div>
                                        <h3 className="text-center">Phone</h3>
                                        <div className="table-responsive">
                                            <table className="table table-striped table-hover">
                                                <thead className="table-dark">
                                                    <tr>
                                                        <th>Field Name</th>
                                                        <th>Value</th>
                                                        <th>Description</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {phones.map((phone, index) => (
                                                        <React.Fragment key={index}>
                                                            <tr className="table-info">
                                                                <td>&nbsp;</td>
                                                                <td><strong>PHONE #{index + 1}</strong></td>
                                                                <td>&nbsp;</td>
                                                            </tr>
                                                            <tr>
                                                                <td><strong>Phone Number</strong></td>
                                                                <td>{phone.Phone || ''}</td>
                                                                <td></td>
                                                            </tr>
                                                            <tr>
                                                                <td><strong>Phone Type</strong></td>
                                                                <td>{mapTo(phoneTypes, phone.PhType)}</td>
                                                                <td></td>
                                                            </tr>
                                                            <tr>
                                                                <td><strong>Direct Inward Dial Number</strong></td>
                                                                <td>{formatArrayValue(phone.DID)}</td>
                                                                <td></td>
                                                            </tr>
                                                            <tr>
                                                                <td><strong>Record Type</strong></td>
                                                                <td>{mapTo(recordTypes, phone.RecType)}</td>
                                                                <td></td>
                                                            </tr>
                                                            <tr>
                                                                <td><strong>IDate</strong></td>
                                                                <td>{formatArrayValue(phone.IDate)}</td>
                                                                <td><em>Date record was first received</em></td>
                                                            </tr>
                                                            <tr>
                                                                <td><strong>ODate</strong></td>
                                                                <td>{formatArrayValue(phone.ODate)}</td>
                                                                <td><em>Date record was last received as connected</em></td>
                                                            </tr>
                                                            <tr>
                                                                <td><strong>Name of Telephone Company Provider</strong></td>
                                                                <td>{formatArrayValue(phone.TelcoName)}</td>
                                                                <td></td>
                                                            </tr>
                                                            <tr>
                                                                <td><strong>Phone Confidence Score</strong></td>
                                                                <td>{phone.PHV || ''} - {mapTo(phoneConfidence, phone.PHV)}</td>
                                                                <td><em>Possible values 1-7 (1: highest confidence, 7: lowest confidence)</em></td>
                                                            </tr>
                                                            <tr>
                                                                <td><strong>Directory Assistance Flag</strong></td>
                                                                <td>{mapTo(daCodes, phone.DACode)}</td>
                                                                <td></td>
                                                            </tr>
                                                        </React.Fragment>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default InfutorPersonDataDisplay;
